import React from "react"
import {
  Alert,
  AlertIcon,
  Heading,
  Text,
  VStack,
  Box,
  Image,
  Button,
  Spinner,
} from "@chakra-ui/react"
import { useRouteLoaderData, useSearchParams, Link } from "react-router-dom"
import GradientBox from "./GradientBox"
import { getStripePricingTableEmbedConfig } from "../lib/stripePricingEmbed"
import { addAlert, removeAlertBySource, AlertType } from "../lib/alerts"
import { CurrentOrgId } from "../lib/store"
import {
  readIntent,
  clearIntent,
  coerceBilling,
  isExpressEligible,
  TIER_LABELS,
  PRICE_DISPLAY,
} from "../lib/signupIntent"
import { Api } from "../lib/api"
import Logo from "../images/horizontal-light.svg"

const { publishableKey, pricingTableId } = getStripePricingTableEmbedConfig()

const PLAN_LIMIT_ALERT_SOURCE = "Plan limit"
const PLAN_LIMIT_ALERT_BODY =
  "You've reached your plan limit. Upgrade to get back to work — no data migration, instant access."

function FullBleed({ children }) {
  return (
    <Box
      w="100vw"
      maxW="100vw"
      position="relative"
      left="50%"
      transform="translateX(-50%)"
      mt={{ base: 6, md: 8 }}
    >
      {children}
    </Box>
  )
}

/**
 * Single-CTA card shown when the user arrived from a marketing CTA with
 * enough intent to bypass the pricing-table embed. Clicking "Continue"
 * POSTs to /api/stripe/checkout-session/ to mint a Stripe Checkout
 * Session, then redirects the browser to the Stripe-hosted page.
 */
function ExpressCheckoutCard({ tier, billing, src, onFallback }) {
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(null)

  const label = TIER_LABELS[tier]
  const price = PRICE_DISPLAY[tier][billing]
  const cadenceLabel = billing === "annual" ? "Annual" : "Monthly"
  // Surface the Scale + annual-only coercion so the user understands why
  // monthly wasn't honored when they clicked from a CTA we don't have a
  // monthly Price ID for.
  const isCoercedToAnnual = tier === "scale_plus"

  const handleContinue = async () => {
    setLoading(true)
    setError(null)
    try {
      const api = new Api()
      const resp = await api.stripeCreateCheckoutSession(tier, billing, src)
      // Best effort: clear the intent once we hand off to Stripe so a
      // back-button hop doesn't re-trigger the same express card with
      // stale params.
      clearIntent()
      window.location.assign(resp.url)
    } catch (e) {
      console.error("Express checkout failed", e)
      setError(
        "We couldn't start checkout. Please try again or pick a plan below.",
      )
      setLoading(false)
    }
  }

  return (
    <Box
      bg="white"
      color="gray.800"
      p={{ base: 6, md: 8 }}
      borderRadius="md"
      maxW="md"
      mx="auto"
      boxShadow="lg"
    >
      <VStack align="stretch" spacing={3}>
        <Heading size="md">
          Continue to checkout — {label}, {cadenceLabel}
        </Heading>
        <Text fontSize="xl" fontWeight="semibold">
          {price}
        </Text>
        {isCoercedToAnnual && (
          <Text fontSize="sm" color="gray.600">
            Scale + is billed annually.
          </Text>
        )}
        {error && (
          <Alert status="error">
            <AlertIcon />
            {error}
          </Alert>
        )}
        <Button
          colorScheme="purple"
          size="lg"
          onClick={handleContinue}
          isLoading={loading}
          loadingText="Redirecting to Stripe"
          data-testid="express-checkout-continue"
        >
          Continue to Stripe
        </Button>
        <Button
          variant="link"
          colorScheme="purple"
          onClick={onFallback}
          isDisabled={loading}
        >
          Or view all plans
        </Button>
      </VStack>
    </Box>
  )
}

export default function ChoosePlan() {
  const user = useRouteLoaderData("auth")
  const [searchParams] = useSearchParams()
  const isInactive = searchParams.get("reason") === "inactive"
  const isCanceled = searchParams.get("canceled") === "1"
  const currentOrgId = CurrentOrgId.getValue()
  const isNewUser = !currentOrgId && !isInactive

  // Express-checkout intent (only relevant for brand-new users arriving
  // from a marketing CTA with ?tier=&billing=). `showExpress` lets the
  // user opt out via "Or view all plans" without clearing the underlying
  // intent on first paint.
  const initialIntent = React.useMemo(() => readIntent(), [])
  const expressEligible = isNewUser && isExpressEligible(initialIntent)
  const [showExpress, setShowExpress] = React.useState(expressEligible)
  const expressBilling = coerceBilling(
    initialIntent.tier,
    initialIntent.billing,
  )

  // Embed `client-reference-id` carries the src tag (when present) so
  // the webhook can attribute even non-express checkouts. Server-side
  // _handle_checkout_completed splits on "__src__" to recover user.id.
  const embedClientRef = initialIntent.src
    ? `${user.id}__src__${initialIntent.src}`
    : user.id

  React.useEffect(() => {
    if (!isInactive) return
    removeAlertBySource(PLAN_LIMIT_ALERT_SOURCE)
    addAlert(AlertType.WARNING, PLAN_LIMIT_ALERT_SOURCE, PLAN_LIMIT_ALERT_BODY)
    return () => {
      removeAlertBySource(PLAN_LIMIT_ALERT_SOURCE)
    }
  }, [isInactive])

  return (
    <GradientBox
      showLogo={false}
      minH="100vh"
      w="100%"
      alignItems="stretch"
      gap={{ base: 3, md: 4 }}
      p={0}
      pt={{ base: 3, md: 4 }}
      px={{ base: 4, md: 8 }}
      pb={0}
    >
      <Image
        src={Logo}
        alt="Blind Insight Logo"
        maxW={{ base: "9rem", md: "11rem" }}
        w="100%"
        objectFit="contain"
        alignSelf="flex-start"
      />

      <VStack align="stretch" spacing={{ base: 2, md: 3 }} w="100%">
        {isNewUser ? (
          <>
            <Heading variant="standard">Choose a plan to get started</Heading>
            <Text variant="subtext">
              Select a plan below to create your organization and start using
              Blind Insight.
            </Text>
            {isCanceled && (
              <Alert
                status="info"
                bg="whiteAlpha.200"
                color="white"
                borderRadius="md"
                data-testid="checkout-canceled-banner"
              >
                <AlertIcon color="white" />
                No charge — pick a plan when you&apos;re ready.
              </Alert>
            )}
          </>
        ) : isInactive ? (
          <>
            <Heading variant="standard">Pick up where you left off</Heading>
            <Text variant="subtext">
              Your datasets and schemas are still up there. Select a plan to
              restore access right away.
            </Text>
            <Button
              as={Link}
              to="/logout"
              variant="outline"
              size="sm"
              alignSelf="flex-start"
              borderColor="whiteAlpha.700"
              color="white"
              _hover={{ bg: "whiteAlpha.200" }}
              _active={{ bg: "whiteAlpha.300" }}
            >
              Log out
            </Button>
          </>
        ) : (
          <>
            <Heading variant="standard">Manage your plan</Heading>
            <Text variant="subtext">
              Upgrade, downgrade, or review what&apos;s included in your current
              plan.
            </Text>
            <Button
              as={Link}
              to="/account"
              variant="outline"
              size="sm"
              alignSelf="flex-start"
              borderColor="whiteAlpha.700"
              color="white"
              _hover={{ bg: "whiteAlpha.200" }}
              _active={{ bg: "whiteAlpha.300" }}
            >
              ← Back to account
            </Button>
          </>
        )}
      </VStack>

      <FullBleed>
        {showExpress ? (
          <Box pt={{ base: 6, md: 8 }} pb={{ base: 10, md: 14 }}>
            <ExpressCheckoutCard
              tier={initialIntent.tier}
              billing={expressBilling}
              src={initialIntent.src}
              onFallback={() => setShowExpress(false)}
            />
          </Box>
        ) : (
          <Box
            bg="white"
            pt={{ base: 6, md: 8 }}
            px={{ base: 4, md: 6, lg: 8 }}
            pb={{ base: 10, md: 14 }}
            w="100%"
          >
            {!user ? (
              <Spinner />
            ) : (
              <stripe-pricing-table
                width="100%"
                style={{
                  display: "block",
                  width: "100%",
                  minHeight: "min(58vh, 640px)",
                }}
                pricing-table-id={pricingTableId}
                publishable-key={publishableKey}
                customer-email={user.email}
                client-reference-id={embedClientRef}
              />
            )}
          </Box>
        )}
      </FullBleed>
    </GradientBox>
  )
}
